import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import styled from 'styled-components';

import { CardImg } from '@sorare/core/src/components/card/CardImg';

import { AnimatingSlot } from './AnimatingSlot';
import { PlaceholderSlot } from './PlaceholderSlot';
import { TeamSlot } from './TeamSlot';
import type {
  CollectionAnimationClub_anyCardInterface,
  CollectionAnimationClub_cardCollection,
} from './__generated__/index.graphql';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--unit);
  padding: var(--double-unit);
  background-color: var(--c-black);
  overflow: hidden;
`;

const ItemWrapper = styled.div`
  aspect-ratio: var(--card-aspect-ratio);
`;

type Props = {
  cardCollection: CollectionAnimationClub_cardCollection;
  animatingCardIndex: number | undefined;
  onCardAnimationEnd: () => void;
  newCards: CollectionAnimationClub_anyCardInterface[];
  ref: (ref: HTMLDivElement, index: number) => void;
  index: number;
};

export const CollectionAnimationClub = ({
  cardCollection,
  animatingCardIndex,
  onCardAnimationEnd,
  newCards,
  ref,
  index,
}: Props) => {
  const { userCardCollection, anyTeam } = cardCollection;
  const slots = userCardCollection?.slots || [];

  const animatingSlots = slots.filter(slot =>
    newCards.some(
      card => card.slug === slot.highlightedCardCollectionCard?.anyCard.slug
    )
  );

  const animatingCard =
    typeof animatingCardIndex === 'number'
      ? newCards.at(animatingCardIndex)
      : undefined;
  const animatingSlotIndex = animatingCard
    ? animatingSlots.findIndex(
        slot =>
          slot.highlightedCardCollectionCard?.anyCard.slug ===
          animatingCard.slug
      )
    : undefined;

  const finalRef = useCallback(
    (r: Nullable<HTMLDivElement>) => {
      if (r) {
        ref(r, index);
      }
    },
    [ref, index]
  );

  return (
    <Grid ref={finalRef}>
      <ItemWrapper key="team">
        <TeamSlot anyTeam={anyTeam} />
      </ItemWrapper>
      {slots.map(slot => {
        const isFilled = !!slot.cardCollectionCardsCount;
        const isNewCardSlot = newCards.some(
          card => card.slug === slot.highlightedCardCollectionCard?.anyCard.slug
        );

        if (isNewCardSlot) {
          const cardPictureUrl =
            slot.highlightedCardCollectionCard?.anyCard.pictureUrl;
          if (!cardPictureUrl) {
            return null;
          }

          return (
            <AnimatingSlot
              key={slot.slug}
              isFirstAnimatingSlot={
                animatingSlots.findIndex(
                  animatingSlot => animatingSlot.slug === slot.slug
                ) === 0
              }
              cardPictureUrl={cardPictureUrl}
              shouldAnimate={
                animatingSlotIndex !== undefined &&
                animatingSlots.at(animatingSlotIndex)?.slug === slot.slug
              }
              onAnimationEnd={onCardAnimationEnd}
            />
          );
        }
        if (isFilled) {
          return (
            <CardImg
              key={slot.slug}
              src={slot.highlightedCardCollectionCard?.anyCard.pictureUrl}
              width={160}
            />
          );
        }
        return <PlaceholderSlot key={slot.slug} slot={slot} />;
      })}
    </Grid>
  );
};

CollectionAnimationClub.fragments = {
  cardCollection: gql`
    fragment CollectionAnimationClub_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        ...TeamSlot_anyTeam
      }
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        slots {
          slug
          highlightedCardCollectionCard {
            id
            anyCard {
              slug
              pictureUrl
            }
          }
          cardCollectionCardsCount
          ...PlaceholderSlot_userCardCollectionSlot
        }
      }
    }
    ${TeamSlot.fragments.anyTeam}
    ${PlaceholderSlot.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionAnimationClub_cardCollection>,
  anyCardInterface: gql`
    fragment CollectionAnimationClub_anyCardInterface on AnyCardInterface {
      slug
    }
  ` as TypedDocumentNode<CollectionAnimationClub_anyCardInterface>,
};
